import moment from 'moment'

/**
 * Helpers null-safe do PGR Bradesco. Segue o padrão já usado pelos demais PDFs
 * customizados (ver bioqualynet/_helpers/pdfHelpers.js e allarmi/_helpers/pdfHelpers.js):
 * nunca renderizar "undefined"/"null" — sempre um fallback textual.
 */
export const FALLBACK = 'N/A'

export const getReplyValue = reply => {
  if (!reply || reply.value === undefined || reply.value === null || reply.value === '') {
    return FALLBACK
  }
  if (reply.question?.type?.name === 'sign') {
    return reply.value?.name || FALLBACK
  }
  // Resposta de opção única do sistema: { id, name }.
  if (typeof reply.value === 'object' && !Array.isArray(reply.value) && reply.value.name !== undefined) {
    return reply.value.name
  }
  return normalizeValueToText(reply.value)
}

/**
 * Valores de opção (única ou múltipla) chegam como { id, name } ou [{ id, name }] —
 * renderizar o objeto direto quebra o React (error #31). Reduz sempre a texto.
 *
 * Campo Adicional do tipo "address" (ver `ADDRESS_TYPE` em
 * `modules/fields/edit/sections/fieldTypes/FieldTypesList.js` e `AddressInput.js`) é UM
 * TERCEIRO FORMATO de objeto, além de opção `{ name }`/`{ value }`: `{ search, complement,
 * ... }` (mesmo shape de `formatAddressToObj`, usado por `serviceOrder.address` e por todo
 * `DynamicPresentation`/`DynamicPresentationText` do app para renderizar campos de
 * endereço — `${search}${complement ? ' -  ' + complement : ''}`). Bug reportado por Julia
 * Buzzo (PM, 2026-09-01): o campo Endereço (Seção 1.1) é um Campo Adicional "address", cujo
 * `value` é esse objeto `{ search, complement }` — sem `.name` nem `.value` — então cai no
 * fallback `value.name ?? value.value ?? FALLBACK` = "N/A" mesmo com o endereço
 * corretamente cadastrado. Diferente do bug de #740 (comparação de ID number vs string,
 * já corrigido em `getFieldValueById` abaixo): aqui o ID casa certo, mas o VALOR nunca
 * era extraído. Corrigido tratando esse shape antes do fallback genérico de opção.
 */
const normalizeValueToText = value => {
  if (Array.isArray(value)) {
    const names = value
      .map(item => (item && typeof item === 'object' ? (item.name ?? item.value) : item))
      .filter(Boolean)
    return names.length ? names.join(', ') : FALLBACK
  }
  if (value && typeof value === 'object') {
    if (value.search !== undefined) {
      return value.search ? `${value.search}${value.complement ? ` -  ${value.complement}` : ''}` : FALLBACK
    }
    return value.name ?? value.value ?? FALLBACK
  }
  return value
}

/** Como getReplyValue, mas devolve o valor "cru" (sem coerção a FALLBACK) quando existir. */
export const getRawReplyValue = reply => {
  if (!reply) return undefined
  if (typeof reply.value === 'object' && reply.value !== null && !Array.isArray(reply.value)) {
    return reply.value.name ?? reply.value.value ?? reply.value
  }
  return reply.value
}

export const findReplyByQuestionId = (replies = [], questionId) => {
  if (questionId === undefined || questionId === null) {
    return undefined
  }
  return replies.find(reply => reply.question?.id === questionId)
}

export const filterRepliesByQuestionIds = (replies = [], questionIds = []) => {
  return replies.filter(reply => questionIds.includes(reply.question?.id))
}

export const findReplyByReference = (replies = [], reference) => {
  return replies.find(reply => reply.question?.reference === reference)
}

export const filterRepliesByParentReference = (replies = [], reference) => {
  return replies.filter(
    reply => reply.question?.parentReference && reply.question.parentReference.indexOf(reference) === 0
  )
}

/**
 * Campo Adicional (fieldValues) por ID do campo — Local ou Ativo, conforme o objeto
 * informado.
 *
 * Comparação por `String(...)` dos dois lados, não `===` direto: a API de campos
 * (`fieldsClient`/`placesClient` — serviço distinto do de Ordens de Serviço, ver
 * `utils/apollo/api.js`) serializa `field.id` como STRING (`ID` do GraphQL, por spec, vem
 * como string na resposta), enquanto as constantes em `mappingIds.js`
 * (`LOCAL_FIELD_ID_ENDERECO` etc.) são number literals — `1 === '1'` é sempre `false` em
 * JS. Bug relatado por Julia Buzzo (PM, 2026-08-30): Endereço/Cidade/UF/CNPJ (Seção
 * 1.1) todos vinham `N/A` numa atividade real, mesmo com o Campo Adicional preenchido —
 * a auditoria anterior (PR #736) só tinha validado esta função com dados fictícios, cujo
 * mock não reproduzia esse detalhe de serialização (`field: { id: 1 }` como number
 * literal já bate direto com a constante, mascarando o bug). Confirmado renderizando o
 * componente real com `field.id` como string (formato real da API) — reproduz o `N/A`
 * antes da correção, resolve depois. Afeta por igual todo Campo Adicional casado por ID
 * neste módulo (Endereço/CNPJ/Quantidade de Funcionários do Local, ID 1/2/5) — corrigido
 * na única função compartilhada por todos eles, não em cada campo individualmente.
 */
export const getFieldValueById = (fieldValues = [], fieldId) => {
  const found = fieldValues.find(fieldValue => String(fieldValue.field?.id) === String(fieldId))
  return found ? normalizeValueToText(found.value) : undefined
}

export const getFieldValueByIdOrFallback = (fieldValues = [], fieldId, fallback = FALLBACK) => {
  const value = getFieldValueById(fieldValues, fieldId)
  return value === undefined || value === null || value === '' ? fallback : value
}

/**
 * Como `getFieldValueById`, mas devolve o valor CRU (objeto, se for o caso) sem passar
 * por `normalizeValueToText` — necessário para ler sub-campos estruturados de um Campo
 * Adicional tipo "address" (`.city`/`.state`/etc.), que se perdem ao normalizar o valor
 * inteiro para uma única string de exibição (ver `getFieldValueById`/`getCidadeUfDoEndereco`
 * abaixo).
 */
export const getFieldRawValueById = (fieldValues = [], fieldId) => {
  const found = fieldValues.find(fieldValue => String(fieldValue.field?.id) === String(fieldId))
  return found ? found.value : undefined
}

/**
 * Nome de um nível da hierarquia de "nível de local" (`place.placeLevelsResume`) por
 * posição — não Campo Adicional. Ver `mappingIds.js` (`LOCAL_PLACE_LEVEL_INDEX_*`) para a
 * convenção de indexação (NÍVEL N, 1-indexado no documento do cliente -> posição N-1 no
 * array, 0-indexado). Mesmo padrão `placeLevelsResume[N].name` já em produção nos PDFs
 * white-label segurpro/comercial/allarmi (`_shared/sections/ClientData.js`).
 */
export const getPlaceLevelNameByIndexOrFallback = (placeLevelsResume, index, fallback = FALLBACK) => {
  const nome = placeLevelsResume?.[index]?.name
  return nome === undefined || nome === null || nome === '' ? fallback : nome
}

/**
 * "Destino da Atividade": o Local selecionado no par de campos "Tipo do destino"/"Destino"
 * da criação da OS (`serviceOrder.place` — ver `GeneralSection.js` em
 * `serviceOrders/editV2/sections/`, onde o campo `place` é rotulado "Destino" e "Tipo do
 * destino" é apenas o filtro de tipos de Local oferecidos no seletor, não um seletor
 * Local/Ativo). "Ativo" (`serviceOrder.asset`) é um campo separado e opcional que refina
 * esse Local (filtrado por `placeIds: form.place?.id`) — não é um destino alternativo, e
 * por isso não compõe esta linha (mesmo padrão de outros PDFs: `serviceOrder.asset` sempre
 * aparece em sua própria linha, nunca dentro da linha de Destino — ver
 * `serviceOrders/pdf/general/Overview.js`).
 *
 * ORDEM corrigida nesta revisão (bug reportado por Julia Buzzo, PM, 2026-09-01): a revisão
 * anterior (PR #739) compunha esta linha como `place.resume + ' - ' + place.name`, por
 * analogia com o padrão usado em outros PDFs customizados (`serviceOrders/pdf/general/
 * Overview.js`, `pdfs/lmdasilva/general/Overview.js`, `pdfs/meta/general/Overview.js`) e
 * com o preset "Nome completo do destino" do seletor de interpolação de mensagens
 * (`utils/interpolations/serviceOrder.js`: `{{service_order.place.resume}} -
 * {{service_order.place.name}}`). Isso produzia a ORDEM INVERTIDA na capa: "SILVA
 * JARDIM-UCTBA - 2160" em vez de "2160 - SILVA JARDIM-UCTBA".
 *
 * Confirmado contra a spec de dados de `places` (`_reversa_sdd/data-dictionary.md`):
 * `place.name` é o nome do NÍVEL FOLHA do Local (aqui, o código numérico da agência,
 * ex. "2160") e `place.resume` é o rótulo breadcrumb pré-renderizado (ex. "SILVA
 * JARDIM-UCTBA" — o nome descritivo da agência, sem ancestrais cadastrados nesta OS). O
 * padrão "resume - name" de Overview.js funciona lá porque, nesses contextos, o breadcrumb
 * (contexto amplo) precede o nome específico; para o Destino da capa do PGR Bradesco, a
 * Julia pediu explicitamente a ordem inversa (código antes do nome descritivo) — por isso
 * este helper NÃO reaproveita a ordem do padrão genérico, mesmo reaproveitando as mesmas
 * duas fontes de dado.
 *
 * ATUALIZAÇÃO (2026-09-02): `place?.name` (a primeira parte desta linha) É literalmente o
 * valor de "Junção" — ver a causa raiz completa em `01.InformacoesIniciaisSection.js`
 * (seção 1.2, coluna JUNÇÃO). Não é a EMPRESA do cliente (`serviceOrder.company`), que
 * também não representa o Destino selecionado na OS; misturar os dois é o que produzia a
 * capa quebrada "N/A - - 2160" no bug original (a implementação de então tentava ler
 * Junção como nível 4 de `place.placeLevelsResume`, ausente nesse Local -> fallback
 * "N/A"; Empresa não cadastrada nesta OS -> vazio; e "2160", já correto, era o único
 * pedaço certo — `place.name` — só exibido sem o `resume` e cercado pelos dois campos
 * errados).
 *
 * Isso também explica o placeholder do PDF-modelo para esta linha (checado 2026-09-01):
 * "Junção/Nome da Dependência - Nome do local da atividade" — exatamente
 * `place.name` (Junção) + `place.resume` (nome do local), a mesma ordem usada aqui. O
 * exemplo concreto e datado da Julia (item 1, 2026-09-01: "2160 - SILVA JARDIM-UCTBA") é a
 * fonte de verdade usada para a ORDEM.
 */
export const getDestinoDaAtividade = (place, fallback = FALLBACK) => {
  const partes = [place?.name, place?.resume].filter(parte => parte !== undefined && parte !== null && parte !== '')
  return partes.length ? partes.join(' - ') : fallback
}

/**
 * Extrai Cidade e UF de um texto de endereço livre (ex.: "Av. Paulista, 1000 - Bela
 * Vista, São Paulo - SP, 01310-100"). Heurística best-effort, com fallback 'N/A'.
 *
 * FALLBACK apenas: usada só quando o valor cru do Campo Adicional não é o objeto
 * estruturado (ver `getCidadeUfDoEndereco` abaixo, fonte preferencial) — ex.: um
 * endereço antigo cadastrado como texto livre puro, antes deste Campo Adicional virar
 * tipo "address". Regra: UF de 2 letras (sigla, ex. "SP") — NÃO casa com o nome
 * completo do estado (ex. "Bahia"), que é o formato normalmente devolvido pelo Google
 * Places (ver `getCidadeUfDoEndereco` — por isso esse é o caminho preferencial, este
 * regex frequentemente não encontra nada em endereços reais).
 */
export const parseCityUfFromAddress = enderecoTexto => {
  if (!enderecoTexto || typeof enderecoTexto !== 'string') {
    return { cidade: FALLBACK, uf: FALLBACK }
  }

  // Padrão "..., Cidade - UF" (com ou sem CEP após a UF).
  const matchTraco = enderecoTexto.match(/,?\s*([\wÀ-ÿ'.\s]+?)\s*-\s*([A-Z]{2})\b(?!\w)/)
  if (matchTraco) {
    return { cidade: matchTraco[1].trim(), uf: matchTraco[2].trim() }
  }

  // Padrão "Cidade/UF".
  const matchBarra = enderecoTexto.match(/([\wÀ-ÿ'.\s]+?)\/([A-Z]{2})\b(?!\w)/)
  if (matchBarra) {
    return { cidade: matchBarra[1].trim(), uf: matchBarra[2].trim() }
  }

  return { cidade: FALLBACK, uf: FALLBACK }
}

/**
 * Cidade e UF a partir do valor CRU do Campo Adicional Endereço (tipo "address").
 *
 * Bug relatado por Julia Buzzo (PM, 2026-09-02): mesmo depois do Endereço passar a
 * exibir corretamente ("Rua Silva Jardim, 942 - Valéria, Salvador - Bahia, Brasil" —
 * fix do `normalizeValueToText` no PR #742), Cidade e UF continuavam "N/A". Causa raiz:
 * ambos dependiam de `parseCityUfFromAddress` fazer um parse por REGEX em cima do TEXTO
 * já concatenado do Endereço (`${search}${complement}`) — que nunca isola Cidade/UF como
 * campos próprios — e a heurística de regex assume UF como sigla de 2 letras (ex. "SP"),
 * enquanto o Google Places (fonte real deste Campo Adicional, ver `AddressInput.js`)
 * devolve o NOME COMPLETO do estado (ex. "Bahia"), que a regex nunca casa — resultado:
 * sempre `N/A`, mesmo com o endereço certinho.
 *
 * Corrigido lendo Cidade/UF DIRETO do objeto estruturado do Campo Adicional (`.city`/
 * `.state`), sem precisar reconstituir do texto já concatenado. Mesmo shape de
 * `formatAddressToObj`/`formatAddressFromGoogleApi` já em produção para o mesmo tipo de
 * campo em outro módulo deste app — ver `pdfs/vci/_shared/sections/TerminationContent.js`
 * (`address.city`/`address.state`/`address.neighborhood`/`address.number`/
 * `address.address` no valor CRU — sem `normalizeValueToText` — de uma pergunta tipo
 * "address"): confirma que `.city`/`.state` são campos reais e confiáveis nesse shape,
 * não uma suposição.
 *
 * Fallback para `parseCityUfFromAddress` (regex sobre o texto já renderizado) só quando
 * o valor cru não é objeto ou não tem `.city`/`.state` preenchidos — ex.: endereço
 * legado cadastrado como texto livre puro, antes deste campo virar tipo "address".
 */
export const getCidadeUfDoEndereco = (enderecoValorBruto, enderecoTexto) => {
  if (enderecoValorBruto && typeof enderecoValorBruto === 'object' && !Array.isArray(enderecoValorBruto)) {
    const cidade = typeof enderecoValorBruto.city === 'string' ? enderecoValorBruto.city.trim() : ''
    const uf = typeof enderecoValorBruto.state === 'string' ? enderecoValorBruto.state.trim() : ''
    if (cidade || uf) {
      return { cidade: cidade || FALLBACK, uf: uf || FALLBACK }
    }
  }
  return parseCityUfFromAddress(enderecoTexto === FALLBACK ? '' : enderecoTexto)
}

export const formatDateBr = data => {
  if (!data) return FALLBACK
  const momento = moment(data)
  return momento.isValid() ? momento.format('DD/MM/YYYY') : FALLBACK
}

/**
 * Data de Elaboração: dia 30 do mês/ano de conclusão da atividade.
 *
 * Meses com menos de 30 dias (fevereiro) não têm "dia 30" — usa o último dia do mês
 * nesse caso. IMPORTANTE: `moment#date(30)` sem esse cuidado rola para o mês seguinte
 * em fevereiro (ex.: conclusão em 15/02/2026 viraria 02/03/2026), o que violaria a
 * regra "dia 30 do MESMO mês" — por isso o dia é limitado a `daysInMonth()`.
 */
export const getDataElaboracao = serviceOrder => {
  const conclusao = serviceOrder?.endDate
  if (!conclusao) return null
  const momento = moment(conclusao)
  if (!momento.isValid()) return null
  const dia = Math.min(30, momento.daysInMonth())
  return momento.clone().date(dia)
}

/**
 * Vigência até: regra corrigida a pedido da Julia ("Ajustes bradesco.pdf") — substitui a
 * regra antiga ("Data de Elaboração + 730 dias"), que não reproduzia o exemplo fornecido
 * pelo cliente. Nova regra, em duas etapas:
 *   1. Soma exatamente 2 anos à Data de Elaboração (mesmo dia e mês, ano + 2).
 *   2. Ajusta o dia para o ÚLTIMO dia válido do mês resultante (respeitando o tamanho do
 *      mês e anos bissextos em fevereiro) — SEMPRE, não só quando o dia "não existir".
 *
 * Exemplo dado pelo cliente: 30/08/2026 → +2 anos → 30/08/2028 → dia ajustado para o
 * último dia de agosto/2028 → 31/08/2028. O passo 2 se aplica incondicionalmente (mesmo
 * quando o dia somado já é válido no mês resultante, ex.: dia 1 vira o fim do mês) —
 * não é apenas um "clamp" de segurança para overflow de mês (esse overflow, aliás, nem
 * ocorre aqui: diferente de `getDataElaboracao`, somar ANOS com `moment#add('years')`
 * mantém o mesmo mês sempre, mesmo em 29/02 bissexto -> ano não-bissexto, quando o
 * moment já teria clampado para 28/02 daquele mesmo mês de destino).
 */
export const getVigenciaAte = dataElaboracao => {
  if (!dataElaboracao) return null
  const maisDoisAnos = dataElaboracao.clone().add(2, 'years')
  return maisDoisAnos.date(maisDoisAnos.daysInMonth())
}

export const getNumeroLimpo = valor => {
  if (typeof valor === 'number') return valor
  if (typeof valor === 'string') {
    const numero = Number(valor.replace(',', '.').replace(/[^\d.-]/g, ''))
    return Number.isNaN(numero) ? undefined : numero
  }
  if (valor && typeof valor === 'object') {
    return getNumeroLimpo(valor.name ?? valor.value)
  }
  return undefined
}
